import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import { useLocation, useNavigate } from "react-router-dom";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { DeletePopup } from "@helpers/popups/DeletePopup";
import TableSkeleton from "@components/layouts/TableSkeleton";
import { TDeleteSuccesResponse } from "@type-defs/general/TDeleteSuccessResponse";
import { Toast } from "@helpers/popups/Toast";
import { useDeleteWarehouseMutation } from "@states/warehouse-and-inventory/warehouse.api";
import useGetAllWarehouses from "@hooks/warehouse-and-inventory/useGetAllWarehouses";
import { TWarehouse } from "@helpers/validation-schemas/warehouse-and-inventory/warehouse.schema";
import { WAREHOUSE } from "@utils/constants/warehouse.constants";
import { allocationAccountColumns } from "@helpers/columns/warehouse-and-inventory/allocation-account.columns";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import NoRecord from "@components/layouts/NoRecord";
import InfoModal from "@components/modals/common/InfoModal";
import WarehouseDetail from "@components/extra/warehouse-and-inventory/WarehouseDetail";
import AllocationAccountFilter from "@components/filters/AllocationAccountFilter";
import AuthComponent from "@components/auth/component";
import useGetPermission from "@hooks/common/useGetPermission";
import { ALLOCATION_ACCOUNT } from "@utils/constants/allocation-account.constants";

const AllocationAccountPage = () => {
  const { hasAccess } = useGetPermission();
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const filterRef = useRef({
    main_warehouse_id: undefined,
  });
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [warehouseDetail, setWarehouseDetail] = useState<
    TWarehouse | undefined
  >(undefined);

  const {
    warehouseData,
    warehouseLoading,
    warehouseError,
    warehousePaginator,
    warehouseRefetch,
    warehouseFetching,
  } = useGetAllWarehouses({
    page,
    limit,
    search: search,
    warehouse_type:2,
    ...filterRef.current,
  });

  const [deleteProductTag, { error: deleteError, isLoading }] =
    useDeleteWarehouseMutation();

  useErrorHandling(
    ...castTypeArr<TFailResponse>([warehouseError, deleteError])
  );

  useEffect(() => {
    if (location.state?.reload) {
      warehouseRefetch();
    }
  }, [location.state?.reload]);

  const toggleModalHandler = (warehouse: TWarehouse) => {
    setWarehouseDetail(warehouse);
    setShowInfoModal(true);
  };

  const editHandler = (warehouse: TWarehouse) => {
    navigate(`${ALLOCATION_ACCOUNT.BASE_PATH}${warehouse.warehouse_id}/edit`, {
      state: {
        warehouse,
      },
    });
  };

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deleteProductTag(id);

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TDeleteSuccesResponse;

        if (success) {
          Toast.fire({
            icon: "success",
            title: message,
          });

          warehouseRefetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AuthComponent
      action="view"
      module="Stock & Inventory"
      subModule="Allocation Account"
      page
    >
      <React.Fragment>
        {(isLoading || warehouseLoading) && <LoadingSpinner />}
        <Search setSearch={setSearch} />
        <PageWrapper
          addPath={ALLOCATION_ACCOUNT.ADD_PATH()}
          addButtonTitle={ALLOCATION_ACCOUNT.ADD_BUTTON_TITLE}
          title={ALLOCATION_ACCOUNT.LIST_PAGE_TITLE}
          hasCreatePermission={hasAccess(
            "create",
            "Stock & Inventory",
            "Allocation Account"
          )}
        >
          <AllocationAccountFilter refetch={warehouseRefetch} filterRef={filterRef} />
          <InfoModal
            title="Allocation Account List Detail"
            isOpen={showInfoModal}
            setIsOpen={setShowInfoModal}
          >
            <WarehouseDetail warehouse={warehouseDetail!} />
          </InfoModal>
          <DataTable
            keyField="warehouse_id"
            responsive
            striped
            pagination
            paginationServer
            //@ts-ignore
            paginationTotalRows={warehousePaginator?.totalItems}
            progressPending={warehouseFetching}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoRecord />}
            progressComponent={<TableSkeleton row={10} actionRight col={4} />}
            //@ts-ignore
            columns={allocationAccountColumns(
              editHandler,
              deleteHandler,
              toggleModalHandler
            )}
            //@ts-ignore
            data={warehouseData}
          />
        </PageWrapper>
      </React.Fragment>
    </AuthComponent>
  );
};

export default AllocationAccountPage;
